import { WorkflowActionDelay } from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowActionDelay";
import { WorkflowTaskCompleteEvent } from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskCompleteEvent";
import {
  getChoicesFromWorkflowConfiguration,
  getEventSchemaIdFromWorkflowSystemActionConfiguration,
  getEventSchemaIdFromWorkflowUserTaskConfiguration,
  getFormSchemaIdFromWorkflowConfiguration,
  getRecordFilterFromWorkflowConfiguration,
  getReportIdFromWorkflowConfiguration,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskDetailDrawer.helpers";
import { WorkflowTaskDetailKeyValueList } from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskDetailKeyValueList";
import { WorkflowTaskFormSubmission } from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskFormSubmission";
import { WorkflowTaskUserChoice } from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskUserChoice";
import { Accordion } from "@validereinc/common-components";
import {
  SystemActionType,
  UserTaskType,
  WorkflowConfigStepType,
  WorkflowConfigurationType,
  WorkflowStepType,
  WorkflowTriggerType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import { WorkflowActionSendNotification } from "../../WorkflowActionSendNotification/WorkflowActionSendNotification";
import { WorkflowActionSendReport } from "../../WorkflowActionSendReport/WorkflowActionSendReport";
import { WorkflowActionAssessTemplatedReportForComments } from "../../WorkflowTaskDetailDrawer/WorkflowActionAssessTemplatedReportForComments/WorkflowActionAssessTemplatedReportForComments";
import { WorkflowActionCreateEvent } from "../../WorkflowTaskDetailDrawer/WorkflowActionCreateEvent/WorkflowActionCreateEvent";
import { WorkflowActionUpdateRecordStatus } from "../../WorkflowTaskDetailDrawer/WorkflowActionUpdateRecordStatus";
import styles from "./WorkflowConfigurationDrawerTaskDetails.module.scss";
import { WorkflowConfigurationGenericSystemActionPreview } from "./WorkflowConfigurationGenericSystemActionPreview";
import { WorkflowConfigurationRunWorkerPreview } from "./WorkflowConfigurationRunWorkerPreview";

const { getAllWorkflowSteps } = Workflows.logic;

const cx = classNames.bind(styles);

const WorkflowConfigurationDrawerTaskDetails = (props: {
  stepId?: string;
  stepDefinition?: WorkflowConfigStepType;
  workflowTemplate?: {
    config?: WorkflowConfigurationType;
    triggers?: WorkflowTriggerType[];
  };
  panelTitle?: string;
}) => {
  const { stepId, stepDefinition, workflowTemplate, panelTitle } = props;

  const isParallelStep = stepDefinition?.type === WorkflowStepType.PARALLEL;

  const overviewProps = useMemo(() => {
    if (!stepId) return;
    const stepDefinition = getAllWorkflowSteps(
      workflowTemplate?.config?.steps
    )?.[stepId];
    //TODO: support parallel steps
    if (stepDefinition?.type === WorkflowStepType.PARALLEL) return;

    const output: Parameters<typeof WorkflowTaskDetailKeyValueList>[0] = {
      type: stepDefinition?.task.type,
      description: stepDefinition?.description,
    };

    if (stepDefinition?.type === "user_task") {
      output.assignee_user = stepDefinition?.assignee_user ?? undefined;
      output.assignee_group = stepDefinition?.assignee_group ?? undefined;
      output.priorityId = stepDefinition?.priority_id ?? undefined;
    }

    return output;
  }, [stepId, workflowTemplate]);

  const choices = useMemo(
    () => getChoicesFromWorkflowConfiguration(stepId, workflowTemplate?.config),
    [stepId, workflowTemplate?.config]
  );

  const formSchemaId = getFormSchemaIdFromWorkflowConfiguration(
    stepId,
    workflowTemplate?.config
  );

  const userTaskEventSchemaId =
    getEventSchemaIdFromWorkflowUserTaskConfiguration(
      stepId,
      workflowTemplate?.config
    );

  const systemActionEventSchemaId =
    getEventSchemaIdFromWorkflowSystemActionConfiguration(
      stepId,
      workflowTemplate?.config
    );

  const recordFilter = getRecordFilterFromWorkflowConfiguration(
    stepId,
    workflowTemplate?.config
  );

  const reportId = getReportIdFromWorkflowConfiguration(
    stepId,
    workflowTemplate?.config
  );

  if (!stepDefinition) return null;

  return (
    <>
      {!isParallelStep && (
        <>
          {stepDefinition?.task.type === UserTaskType.SUBMIT_FORM && (
            <WorkflowTaskFormSubmission formSchemaId={formSchemaId} />
          )}

          {stepDefinition?.task.type === UserTaskType.CHOICE && (
            <WorkflowTaskUserChoice
              readOnly
              choices={choices}
            />
          )}

          {stepDefinition?.task.type === UserTaskType.COMPLETE_EVENT && (
            <WorkflowTaskCompleteEvent
              shouldOnlyShowEventSchemaAndCategory
              eventSchemaId={userTaskEventSchemaId}
            />
          )}

          {stepDefinition?.task.type === SystemActionType.DELAY && (
            <WorkflowActionDelay
              workflowConfiguration={workflowTemplate?.config}
              stepId={stepId}
            />
          )}

          {stepDefinition?.task.type === SystemActionType.CREATE_EVENT && (
            <WorkflowActionCreateEvent
              eventSchemaId={systemActionEventSchemaId ?? ""}
            />
          )}

          {(stepDefinition?.task.type === SystemActionType.LOCK_RECORD ||
            stepDefinition?.task.type === SystemActionType.UNLOCK_RECORD) && (
            <WorkflowActionUpdateRecordStatus
              recordFilter={recordFilter ?? {}}
            />
          )}

          {stepDefinition?.task.type ===
            SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS && (
            <WorkflowActionAssessTemplatedReportForComments
              reportId={reportId ?? ""}
            />
          )}

          {stepDefinition?.task.type === SystemActionType.SEND_NOTIFICATION && (
            <WorkflowActionSendNotification
              users={stepDefinition?.task.users ?? []}
              groups={stepDefinition?.task.groups ?? []}
              message={stepDefinition?.task.message ?? ""}
              title={stepDefinition?.task.title ?? ""}
            />
          )}

          {stepDefinition?.task.type === SystemActionType.SEND_REPORT && (
            <WorkflowActionSendReport
              users={stepDefinition?.task.users ?? []}
              groups={stepDefinition?.task.groups ?? []}
              message={stepDefinition?.task.message ?? ""}
              title={stepDefinition?.task.title ?? ""}
              reportId={stepDefinition?.task.report_id ?? ""}
              reportFilter={stepDefinition?.task.report_filter ?? {}}
            />
          )}

          {stepDefinition?.task.type === SystemActionType.RUN_WORKER && (
            <WorkflowConfigurationRunWorkerPreview
              workerId={stepDefinition.task.worker_id}
              workerVersionId={stepDefinition.task.worker_version_id}
              workerRunInput={stepDefinition.task.worker_run_input}
              output={stepDefinition.task.output}
            />
          )}

          {stepDefinition?.task.type ===
            SystemActionType.GENERIC_SYSTEM_ACTION && (
            <WorkflowConfigurationGenericSystemActionPreview
              operation={stepDefinition.task.operation}
              input={stepDefinition.task.input}
              output={stepDefinition.task.output}
            />
          )}
        </>
      )}

      <Accordion
        key={"overview"}
        className={cx("accordion")}
        defaultActiveKeys={["overview"]}
      >
        <Accordion.AccordionPanel
          dataKey={"overview"}
          className={cx("accordion-panel")}
          title={panelTitle ?? "Overview"}
        >
          <WorkflowTaskDetailKeyValueList {...overviewProps} />
        </Accordion.AccordionPanel>
      </Accordion>
    </>
  );
};

export default WorkflowConfigurationDrawerTaskDetails;
